import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Avatar,
  Tooltip,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentsIcon from "@mui/icons-material/Payments";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip, // ✅ alias para evitar conflicto
  Legend,
} from "chart.js";

// Registrar Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Title,
  ChartTooltip, // ✅ usás el alias
  Legend
);

const FinanzasDashboard = ({ reservations }) => {
  const uniqueReservations = reservations.filter(
    (res, index, self) =>
      index === self.findIndex((r) => r.reservationId === res.reservationId)
  );

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredReservations, setFilteredReservations] =
    useState(uniqueReservations);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(value);

  const handleFilter = () => {
    const filtered = uniqueReservations.filter((reservation) => {
      const checkInDate = new Date(reservation.startDate);
      return (
        (!startDate || checkInDate >= new Date(startDate)) &&
        (!endDate || checkInDate <= new Date(endDate))
      );
    });
    setFilteredReservations(filtered);
  };

  const Caja = filteredReservations.reduce(
    (acc, res) => acc + (res.seña || 0),
    0
  );
  const paymentsPending = filteredReservations.reduce(
    (acc, res) => acc + (res.amount - (res.seña || 0)),
    0
  );
  const totalIncome = filteredReservations.reduce(
    (acc, res) => acc + (res.amount || 0),
    0
  );
  const ingresosGian = filteredReservations.reduce(
    (acc, res) => acc + (res.impu || 0),
    0
  );
  const totalReservas = filteredReservations.length;
  const promedioReserva = totalIncome / (totalReservas || 1);
  const utilidadNeta = totalIncome - ingresosGian;

  const amountPendingData = filteredReservations.map((res) => ({
    name: res.fullName || "Sin Nombre",
    pending: Math.max(res.amount - (res.seña || 0), 0),
    paid: Math.min(res.seña || 0, res.amount),
  }));

  const chartData = {
    labels: amountPendingData.map((item) => item.name),
    datasets: [
      {
        label: "Pagado",
        data: amountPendingData.map((item) => item.paid),
        backgroundColor: "#4CAF50",
      },
      {
        label: "Deuda",
        data: amountPendingData.map((item) => item.pending),
        backgroundColor: "#FF5722",
      },
    ],
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#f5f5f5",
        borderRadius: 2,
        boxShadow: 3,
        minHeight: "100%",
      }}
    >
      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Fecha Inicio"
            type="date"
            fullWidth
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Fecha Fin"
            type="date"
            fullWidth
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            variant="contained"
            fullWidth
            color="primary"
            onClick={handleFilter}
          >
            Filtrar
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={2}>
        <MetricCard
          icon={<PaymentsIcon sx={{ color: "#6A1B9A" }} />}
          label="Dinero en Caja"
          value={formatCurrency(Caja)}
        />
        <MetricCard
          icon={<AttachMoneyIcon sx={{ color: "#C62828" }} />}
          label="Pagos Pendientes"
          value={formatCurrency(paymentsPending)}
          alert={paymentsPending > 0}
        />
        <MetricCard
          icon={<AccountBalanceWalletIcon sx={{ color: "#6A1B9A" }} />}
          label="Ingresos Totales"
          value={formatCurrency(totalIncome)}
        />
     
        <MetricCard
          label="Utilidad Neta"
          value={formatCurrency(utilidadNeta)}
        />
        <MetricCard
          label="Promedio x Reserva"
          value={formatCurrency(promedioReserva)}
        />
        <MetricCard label="Total de Reservas" value={totalReservas} />
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ overflowX: "auto" }}>
        <Typography variant="h6" gutterBottom>
          Comparativa Cobros por Cliente
        </Typography>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            scales: {
              y: {
                type: "logarithmic",
                beginAtZero: true,
                title: {
                  display: true,
                  text: "Monto (escala log)",
                },
                ticks: {
                  callback: function (value) {
                    return "$" + value;
                  },
                },
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

// 🧩 Componente para métricas con íconos y alertas
const MetricCard = ({ icon, label, value, alert }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Card
      sx={{
        borderLeft: alert ? "5px solid red" : "5px solid #1976d2",
        backgroundColor: alert ? "#fff8f6" : "#ffffff",
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          {icon && <Avatar sx={{ bgcolor: "#e3f2fd" }}>{icon}</Avatar>}
          <Box>
            <Typography variant="body2" color="textSecondary">
              {label}
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {value}
              {alert && (
                <Tooltip title="Deudas activas">
                  <WarningAmberIcon
                    color="warning"
                    sx={{ fontSize: 18, ml: 1 }}
                  />
                </Tooltip>
              )}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

export default FinanzasDashboard;
